import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Save, Eye, EyeOff, FileText, GripVertical } from 'lucide-react';

const MarkdownEditorModal = ({
  isVisible,
  onClose,
  onSave,
  initialContent = '',
  title = 'Edit Presentation'
}) => {
  const [content, setContent] = useState(initialContent);
  const [isPreview, setIsPreview] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Window-style position + size so the user can see the presentation behind/around the editor.
  // Defaults: docked to the right side, ~half viewport width, full available height.
  const initialW = Math.min(720, Math.max(380, Math.round((typeof window !== 'undefined' ? window.innerWidth : 1200) * 0.5)));
  const initialH = Math.max(400, Math.round((typeof window !== 'undefined' ? window.innerHeight : 800) * 0.85));
  const initialX = (typeof window !== 'undefined' ? window.innerWidth - initialW - 16 : 100);
  const initialY = 60;
  const [pos, setPos] = useState({ x: initialX, y: initialY });
  const [size, setSize] = useState({ w: initialW, h: initialH });
  const dragState = useRef(null); // { mode: 'move'|'resize', startX, startY, startPos, startSize }

  useEffect(() => {
    setContent(initialContent);
    setHasChanges(false);
  }, [initialContent]);

  useEffect(() => {
    setHasChanges(content !== initialContent);
  }, [content, initialContent]);

  // Intercept Cmd/Ctrl+S to trigger onSave instead of browser Save As
  useEffect(() => {
    if (!isVisible) return;
    const onKeyDown = (e) => {
      const isSaveCombo = (e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 's';
      if (isSaveCombo) {
        e.preventDefault();
        e.stopPropagation();
        handleSave();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isVisible, content, isSaving]);

  const handleSave = async () => {
    if (isSaving) return; // Prevent double-clicks
    
    try {
      setIsSaving(true);
      await onSave(content);
      setHasChanges(false);
    } catch (error) {
      console.error('Save failed:', error);
      // Don't close the modal if save failed
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
        setContent(initialContent);
        setHasChanges(false);
      }
    } else {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // --- Drag & resize ---
  const onPointerMove = useCallback((e) => {
    const s = dragState.current;
    if (!s) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (s.mode === 'move') {
      const maxX = window.innerWidth - 80;   // keep at least a strip on screen
      const maxY = window.innerHeight - 80;
      setPos({
        x: Math.max(-size.w + 80, Math.min(maxX, s.startPos.x + dx)),
        y: Math.max(0, Math.min(maxY, s.startPos.y + dy))
      });
    } else if (s.mode === 'resize') {
      const minW = 320, minH = 240;
      const maxW = Math.max(minW, window.innerWidth - pos.x);
      const maxH = Math.max(minH, window.innerHeight - pos.y);
      setSize({
        w: Math.max(minW, Math.min(maxW, s.startSize.w + dx)),
        h: Math.max(minH, Math.min(maxH, s.startSize.h + dy))
      });
    }
  }, [pos.x, pos.y, size.w]);

  const onPointerUp = useCallback(() => {
    dragState.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove]);

  // All hooks declared. Early-return AFTER hooks so React sees the same hook count every render.
  if (!isVisible) return null;

  const startDrag = (mode) => (e) => {
    // Don't initiate drag from interactive elements inside the header
    if (e.target.closest('button, input, textarea')) return;
    e.preventDefault();
    dragState.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      startPos: { ...pos },
      startSize: { ...size }
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const styles = {
    // Backdrop is purely an event passthrough — no visual layer, so the user can
    // see (and interact with) the presentation around the floating editor window.
    backdrop: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 2000,
      pointerEvents: 'none'
    },
    modal: {
      position: 'fixed',
      left: `${pos.x}px`,
      top: `${pos.y}px`,
      width: `${size.w}px`,
      height: `${size.h}px`,
      pointerEvents: 'auto',
      background: 'linear-gradient(135deg, rgba(15, 15, 35, 0.92), rgba(26, 26, 46, 0.92))',
      backdropFilter: 'blur(20px)',
      color: 'white',
      borderRadius: '16px',
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      boxShadow: '0 16px 48px rgba(0, 0, 0, 0.5)',
      overflow: 'hidden',
      resize: 'none'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.9rem 1.25rem',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      background: 'rgba(255, 255, 255, 0.05)',
      cursor: 'move',
      userSelect: 'none',
      touchAction: 'none'
    },
    resizeHandle: {
      position: 'absolute',
      right: 0,
      bottom: 0,
      width: '18px',
      height: '18px',
      cursor: 'nwse-resize',
      touchAction: 'none',
      background: 'linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0.25) 60%, transparent 60%, transparent 70%, rgba(255,255,255,0.25) 70%, rgba(255,255,255,0.25) 80%, transparent 80%)',
      borderBottomRightRadius: '16px'
    },
    titleSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    titleIcon: {
      width: '40px',
      height: '40px',
      background: 'linear-gradient(135deg, #00d4ff, #9333ea)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      margin: 0
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    iconButton: {
      width: '40px',
      height: '40px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      borderRadius: '10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      transition: 'all 0.2s ease'
    },
         saveButton: {
       background: (hasChanges || isSaving)
         ? 'linear-gradient(135deg, #00d4ff, #9333ea)' 
         : 'rgba(255, 255, 255, 0.1)',
       border: (hasChanges || isSaving)
         ? '1px solid rgba(0, 212, 255, 0.3)' 
         : '1px solid rgba(255, 255, 255, 0.15)',
       padding: '0.75rem 1.5rem',
       borderRadius: '12px',
       cursor: (hasChanges && !isSaving) ? 'pointer' : 'not-allowed',
       display: 'flex',
       alignItems: 'center',
       gap: '0.5rem',
       color: 'white',
       fontSize: '0.9rem',
       fontWeight: '500',
       opacity: (hasChanges || isSaving) ? 1 : 0.5,
       transition: 'all 0.2s ease'
     },
    content: {
      flex: 1,
      display: 'flex',
      overflow: 'hidden'
    },
    editor: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      borderRight: isPreview ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
    },
    textarea: {
      flex: 1,
      background: 'rgba(0, 0, 0, 0.3)',
      border: 'none',
      color: 'white',
      padding: '1.5rem',
      fontSize: '14px',
      fontFamily: '"Fira Code", "JetBrains Mono", monospace',
      lineHeight: '1.6',
      resize: 'none',
      outline: 'none'
    },
    preview: {
      flex: 1,
      padding: '1.5rem',
      background: 'rgba(255, 255, 255, 0.02)',
      overflow: 'auto',
      fontSize: '14px',
      lineHeight: '1.6'
    },
    status: {
      padding: '0.75rem 2rem',
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      background: 'rgba(255, 255, 255, 0.02)',
      fontSize: '0.85rem',
      color: 'rgba(255, 255, 255, 0.6)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }
  };

  const renderPreview = () => {
    // Simple markdown preview (basic implementation)
    return content
      .split('\n')
      .map((line, index) => {
        if (line.startsWith('# ')) {
          return <h1 key={index} style={{ color: '#00d4ff', marginBottom: '1rem' }}>{line.slice(2)}</h1>;
        }
        if (line.startsWith('## ')) {
          return <h2 key={index} style={{ color: '#9333ea', marginBottom: '0.75rem' }}>{line.slice(3)}</h2>;
        }
        if (line.startsWith('### ')) {
          return <h3 key={index} style={{ color: 'white', marginBottom: '0.5rem' }}>{line.slice(4)}</h3>;
        }
        if (line.trim() === '') {
          return <br key={index} />;
        }
        return <p key={index} style={{ marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.8)' }}>{line}</p>;
      });
  };

  return (
    <div style={styles.backdrop}>
      <div style={styles.modal}>
        {/* Header — also acts as drag handle */}
        <div style={styles.header} onPointerDown={startDrag('move')}>
          <div style={styles.titleSection}>
            <div style={styles.titleIcon}>
              <FileText size={20} />
            </div>
            <h2 style={styles.title}>{title}</h2>
            {hasChanges && (
              <span style={{ 
                color: '#00d4ff', 
                fontSize: '0.8rem', 
                background: 'rgba(0, 212, 255, 0.2)', 
                padding: '0.25rem 0.5rem', 
                borderRadius: '6px' 
              }}>
                ● Unsaved
              </span>
            )}
          </div>
          
          <div style={styles.headerActions}>
            <button
              style={styles.iconButton}
              onClick={() => setIsPreview(!isPreview)}
              onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
              onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
            >
              {isPreview ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
            
                         <button
               style={styles.saveButton}
               onClick={(hasChanges && !isSaving) ? handleSave : undefined}
               disabled={!hasChanges || isSaving}
               onMouseOver={(e) => {
                 if (hasChanges && !isSaving) {
                   e.target.style.transform = 'translateY(-1px)';
                   e.target.style.boxShadow = '0 4px 20px rgba(0, 212, 255, 0.3)';
                 }
               }}
               onMouseOut={(e) => {
                 e.target.style.transform = 'translateY(0)';
                 e.target.style.boxShadow = 'none';
               }}
             >
               <Save size={16} />
               {isSaving ? 'Saving...' : 'Save Changes'}
             </button>
            
            <button
              style={styles.iconButton}
              onClick={handleClose}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClose();
              }}
              onMouseOver={(e) => e.target.style.background = 'rgba(255, 0, 0, 0.2)'}
              onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Editor */}
          <div style={styles.editor}>
            <textarea
              style={styles.textarea}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={(e) => {
                const isSaveCombo = (e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 's';
                if (isSaveCombo) {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSave();
                }
              }}
              placeholder="# Your Presentation Title

## Slide 1
Content for your first slide...

## Slide 2
Content for your second slide..."
              spellCheck={false}
            />
          </div>

          {/* Preview */}
          {isPreview && (
            <div style={styles.preview}>
              {renderPreview()}
            </div>
          )}
        </div>

        {/* Status */}
                 <div style={styles.status}>
           <span>
             {content.split('\n').length} lines, {content.length} characters
           </span>
           <span>
             {isPreview ? 'Preview Mode' : 'Edit Mode'} • {
               isSaving ? '☁️ Saving to cloud...' :
               hasChanges ? 'Modified' :
               '☁️ Saved'
             }
           </span>
         </div>
        {/* Resize handle (bottom-right) */}
        <div style={styles.resizeHandle} onPointerDown={startDrag('resize')} />
      </div>
    </div>
  );
};

export default MarkdownEditorModal; 